<template>
  <BaseDialog
    :open="open"
    :disabled="loading || localLoading"
    :max-width="(isAdmin || isTeacher) ? '1000px' : '800px'"
    aria-label="Meldung ansehen"
    @close="$emit('close')"
  >
    <template #title>
      Meldung<span
        v-if="subjectText"
        class="font-normal text-small"
      > — {{ subjectText }}</span>
    </template>

    <div class="content">
      <!-- Tabs: Eltern-Thread -->
      <div
        v-if="thread.length > 1"
        class="tabs"
      >
        <button
          v-for="(n, idx) in thread"
          :key="n.id || idx"
          :class="['tab', {active: idx === activeThreadIndex}]"
          @click="selectThread(idx)"
        >
          <span class="tab-title">#{{ idx + 1 }}</span>
          <i
            v-if="hasAttachmentFlag(n)"
            class="pi pi-envelope"
            aria-hidden="true"
            :title="'Anhang vorhanden'"
            style="font-size:12px;color:var(--info);vertical-align: baseline;"
          />
          <span class="tab-meta">
            {{ fmtDateTime(n.sentAt ?? n.sent_at ?? n.createdAt ?? n.created_at) }}
          </span>
        </button>
      </div>

      <div
        v-if="(isAdmin || isTeacher)"
        class="meta-reasons-grid"
      >
        <div class="meta">
          <div class="meta-row">
            <span class="label">Schüler:</span>
            <span class="value">{{ studentName }}</span>
          </div>
          <div class="meta-row">
            <span class="label">Zeitraum:</span>
            <span class="value">{{ dateRange }}</span>
          </div>
          <div
            v-if="!isParent"
            class="meta-row"
          >
            <span class="label">Absender:</span>
            <span class="value">{{ sender }}</span>
          </div>
          <div class="meta-row">
            <span class="label">gesendet am:</span>
            <span class="value">{{ sentAtFormatted }}</span>
          </div>
          <div
            v-if="!isParent"
            class="meta-row"
          >
            <span class="label">Status am:</span>
            <span class="value">{{ statusLabel }} — {{ updatedAtFormatted }}</span>
          </div>
        </div>

        <div
          class="reasons-card"
          aria-label="Status-Historie"
        >
          <div class="reasons-header">
            <span class="reasons-title">Status-Historie</span>
          </div>
          <div
            v-if="reasonsLoading"
            class="reasons-muted"
          >
            Lädt …
          </div>
          <template v-else>
            <ul
              v-if="reasons.length"
              class="reasons-list"
            >
              <li
                v-for="(r, idx) in reasons"
                :key="r.id || idx"
                class="reasons-item"
              >
                <div class="reasons-line">
                  <span
                    class="badge"
                    :class="{
                      'badge-success': String(r.status).toLowerCase() === 'akzeptiert',
                      'badge-danger': String(r.status).toLowerCase() === 'abgelehnt'
                    }"
                  >{{ r.status }}</span>
                  <span class="reasons-user">{{ r.userName || r.username || '—' }}</span>
                  <span class="reasons-time">{{ fmtDateTime(r.createdAt || r.created_at) }}</span>
                </div>
                <p class="reason-text">
                  {{ r.reason }}
                </p>
              </li>
            </ul>
            <p
              v-else
              class="reasons-muted"
            >
              Keine Einträge.
            </p>
          </template>
        </div>
      </div>
      <div
        v-else
        class="meta"
      >
        <div class="meta-row">
          <span class="label">Schüler:</span>
          <span class="value">{{ studentName }}</span>
        </div>
        <div class="meta-row">
          <span class="label">Zeitraum:</span>
          <span class="value">{{ dateRange }}</span>
        </div>
        <div
          v-if="!isParent"
          class="meta-row"
        >
          <span class="label">Absender:</span>
          <span class="value">{{ sender }}</span>
        </div>
        <div class="meta-row">
          <span class="label">gesendet am:</span>
          <span class="value">{{ sentAtFormatted }}</span>
        </div>
        <div class="meta-row">
          <span class="label">Status am:</span>
          <span class="value">{{ statusLabel }} — {{ updatedAtFormatted }}</span>
        </div>
      </div>

      <div
        v-if="message"
        class="meta"
      >
        <p class="message">
          {{ message }}
        </p>
      </div>

      <div
        v-if="attachments.length"
        class="thumb-strip"
        aria-label="Anhänge"
      >
        <div
          v-for="(att, idx) in attachments"
          :key="att.url || att.id || idx"
          class="thumb"
          role="button"
          tabindex="0"
          :title="`Öffnen: ${att.name}`"
          @click="openAttachment(att)"
          @keydown.enter="openAttachment(att)"
        >
          <img
            v-if="att.kind === 'image' && att.previewUrl"
            :src="att.previewUrl"
            :alt="att.name"
            loading="lazy"
          >
          <div
            v-else
            class="thumb-doc"
          >
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M6 2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm7 1.5V8h4.5L13 3.5zM7.75 18.5h8.5c.69 0 1.25-.56 1.25-1.25v-5.5c0-.69-.56-1.25-1.25-1.25h-8.5c-.69 0-1.25.56-1.25 1.25v5.5c0 .69.56 1.25 1.25 1.25z"
              />
              <text
                x="12"
                y="16"
                text-anchor="middle"
                font-size="6"
                fill="currentColor"
                font-weight="700"
              >PDF
              </text>
            </svg>
            <span class="name">{{ att.name }}</span>
          </div>
        </div>
      </div>

      <!-- Chat-Bereich: nur, wenn die Meldung von Eltern erstellt wurde (creatorRoleId=3) -->
      <div
        v-if="showChat"
        class="chat-card"
        aria-label="Nachrichten"
      >
        <div class="chat-header">
          <span class="chat-title">Nachrichten</span>
          <span
            v-if="unreadChatCount > 0"
            class="badge badge-info"
            title="Ungelesene Nachrichten"
          >Neu</span>
        </div>
        <div
          class="chat-list"
          role="log"
          aria-live="polite"
        >
          <template v-if="chatLoading">
            <p class="muted">
              Lädt …
            </p>
          </template>
          <template v-else>
            <p
              v-if="!chatMessages.length"
              class="muted"
            >
              Keine Nachrichten.
            </p>
            <div
              v-for="m in chatMessages"
              :key="m.id"
              :class="['chat-item', String(m.senderUserId) === String(userId || '') ? 'me' : 'peer']"
            >
              <div class="bubble">
                <div class="chat-meta">
                  <span class="chat-user">{{ m.senderName || '—' }}</span>
                  <span class="chat-time">{{ fmtDateTime(m.createdAt) }}</span>
                </div>
                <p class="chat-text">
                  {{ m.message }}
                </p>
              </div>
            </div>
          </template>
        </div>
        <div class="chat-input-row">
          <input
            v-model.trim="chatInput"
            type="text"
            class="input"
            placeholder="Nachricht schreiben …"
            :disabled="sending || localLoading || loading"
            @keyup.enter="sendChat"
          >
          <button
            class="btn btn-primary"
            :disabled="!chatInput || sending || localLoading || loading"
            title="Senden"
            @click="sendChat"
          >
            Senden
          </button>
        </div>
      </div>
    </div>

    <!-- Grund-Pflichtdialog für Akzeptieren/Ablehnen -->
    <div
      v-if="reasonDialog.open"
      class="reason-overlay"
      role="dialog"
      aria-modal="true"
      @keydown.esc="cancelReason"
    >
      <div class="reason-dialog">
        <h2 class="reason-title">
          {{ reasonDialog.mode === 'akzeptiert' ? 'Grund für Akzeptieren' : 'Grund für Ablehnen' }}
        </h2>
        <div class="form">
          <textarea
            id="reason-input"
            v-model.trim="reasonDialog.text"
            class="input"
            required
            rows="2"
          />
          <p
            v-if="reasonError"
            class="help danger"
            role="alert"
          >
            Bitte einen Grund eingeben.
          </p>
        </div>
        <div class="footer-actions">
          <button
            class="btn"
            :disabled="actioning"
            @click="cancelReason"
          >
            Abbrechen
          </button>
          <button
            class="btn"
            :class="reasonDialog.mode === 'akzeptiert' ? 'btn-success' : 'btn-danger'"
            :disabled="!reasonDialog.text || actioning"
            @click="confirmReason"
          >
            {{ reasonDialog.mode === 'akzeptiert' ? 'Akzeptieren' : 'Ablehnen' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Lösch-Bestätigung -->
    <div
      v-if="deleteDialog.open"
      class="reason-overlay"
      role="dialog"
      aria-modal="true"
      @keydown.esc="cancelDelete"
    >
      <div class="reason-dialog">
        <h2 class="reason-title">
          Löschen bestätigen
        </h2>
        <p class="reason-text">
          Möchten Sie die Meldung für {{ studentName }} vom {{ createdAtOnlyFormatted }} wirklich löschen?
        </p>
        <div class="footer-actions">
          <button
            class="btn"
            :disabled="actioning"
            @click="cancelDelete"
          >
            Abbrechen
          </button>
          <button
            class="btn btn-danger"
            :disabled="actioning"
            @click="confirmDelete"
          >
            Löschen
          </button>
        </div>
      </div>
    </div>

    <!-- Galerie/Viewer Overlay -->
    <div
      v-if="viewer.open"
      class="viewer-overlay"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      @keydown.esc="closeViewer"
    >
      <div class="viewer">
        <div class="viewer-header">
          <span class="viewer-title">{{ currentFileName }}</span>
          <div class="viewer-actions">
            <button
              class="btn"
              :disabled="viewerLoading || !canPrint"
              title="Drucken"
              @click="printCurrent"
            >
              Drucken
            </button>
            <button
              class="btn btn-primary"
              :disabled="viewerLoading"
              title="Download"
              @click="downloadCurrent"
            >
              Download
            </button>
            <button
              class="btn"
              aria-label="Schließen"
              title="Schließen"
              @click="closeViewer"
            >
              Schließen
            </button>
          </div>
        </div>
        <div class="viewer-body">
          <div
            v-if="viewerLoading"
            class="viewer-loading"
          >
            Lädt …
          </div>
          <template v-else>
            <img
              v-if="isCurrentImage && currentDataUrl"
              :src="currentDataUrl"
              alt=""
              class="viewer-media"
            >
            <iframe
              v-else-if="isCurrentPdf && currentObjectUrl"
              :src="currentObjectUrl"
              title="Dokument"
              class="viewer-frame"
            />
            <div
              v-else
              class="viewer-fallback"
            >
              <p class="muted">
                Vorschau nicht verfügbar. Sie können die Datei herunterladen.
              </p>
            </div>
          </template>

          <button
            class="nav prev"
            :disabled="attachments.length <= 1"
            aria-label="Vorheriger"
            @click="prevInViewer"
          >
            ‹
          </button>
          <button
            class="nav next"
            :disabled="attachments.length <= 1"
            aria-label="Nächster"
            @click="nextInViewer"
          >
            ›
          </button>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="footer-actions">
        <!-- Links: Entschuldigung -->
        <div class="footer-left">
          <button
            v-if="isAdmin || isTeacher"
            class="btn btn-primary"
            :disabled="loading || localLoading"
            title="Entschuldigungs-PDF erstellen"
            @click="onGenerateExcusePdf"
          >
            Meldezettel erstellen
          </button>
        </div>

        <!-- Rechts: restliche Aktionen -->
        <div class="footer-right">
          <button
            class="btn btn-cancel"
            :disabled="loading"
            title="Abbrechen"
            @click="$emit('close')"
          >
            <i
              class="pi pi-times"
              aria-hidden="true"
              title="Abbrechen"
            />
            <span class="btn-label">Abbrechen</span>
          </button>

          <div
            v-if="canModerate && isViewingLatest"
            class="btn-group"
            role="group"
            aria-label="Aktion wählen"
          >
            <button
              class="btn btn-danger"
              :disabled="loading || actioning"
              title="Ablehnen"
              @click="openReason('abgelehnt')"
            >
              <span
                class="icon"
                aria-hidden="true"
              >
                <i
                  class="pi pi-times-circle"
                  aria-hidden="true"
                  title="Ablehnen"
                />
              </span>
              <span class="btn-label">Ablehnen</span>
            </button>
            <button
              class="btn btn-success"
              :disabled="loading || actioning"
              title="Akzeptieren"
              @click="openReason('akzeptiert')"
            >
              <span
                class="icon"
                aria-hidden="true"
              >
                <i
                  class="pi pi-check"
                  aria-hidden="true"
                  title="Akzeptieren"
                />
              </span>
              <span class="btn-label">Akzeptieren</span>
            </button>
          </div>
          <button
            v-if="canEditLatest && isViewingLatest"
            class="btn btn-warn"
            :disabled="loading || actioning"
            title="Korrekturmeldung erstellen"
            @click="onCreateCorrection"
          >
            <span
              class="icon"
              aria-hidden="true"
            >
              <i
                class="pi pi-wrench"
                aria-hidden="true"
                title="Korrektur"
              />
            </span>
            <span class="btn-label">Korrektur</span>
          </button>
          <button
            v-if="isAdmin || isTeacher"
            class="btn btn-danger"
            :disabled="loading || actioning"
            title="Meldung löschen"
            @click="onDelete"
          >
            <span
              class="icon"
              aria-hidden="true"
            >
              <i
                class="pi pi-trash"
                aria-hidden="true"
                title="Löschen"
              />
            </span>
            <span class="btn-label">Löschen</span>
          </button>
        </div>
      </div>
    </template>
  </BaseDialog>
</template>

<script setup>
import {computed, ref, watch, nextTick} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import api from '@/services/api';
import {useToast} from 'primevue/usetoast';
import {useAuthStore} from '@/stores/auth';
import {jsPDF} from 'jspdf';

const auth = useAuthStore();
const roleId = computed(() => auth.user?.roleId ?? auth.user?.role_id ?? null);
const userId = computed(() => auth.user?.id ?? auth.user?.userId ?? auth.user?.user_id ?? null);
const isAdmin = computed(() => roleId.value === 1);
const isParent = computed(() => roleId.value === 3);
const isTeacher = computed(() => roleId.value === 2);

// Permissions des aktuellen Nutzers
const myPermissionKeys = ref([]); // lower-case keys
const canModerate = computed(() => isAdmin.value || isTeacher.value);

const props = defineProps({
  open: {type: Boolean, default: false},
  notification: {type: Object, default: () => ({})},
  notificationId: {type: [String, Number], default: null},
  loading: {type: Boolean, default: false}
});

const emit = defineEmits(['close', 'accept', 'reject', 'read', 'create-correction']);

const localLoading = ref(false);
const localNotification = ref(null);

// Ermittelt die aktuelle Notification-ID (lokal ausgewählte hat Vorrang, danach Prop notificationId)
const currentId = computed(() => {
  const id = localNotification.value?.id ?? props.notificationId ?? props.notification?.id ?? null;
  const n = Number(id);
  return Number.isFinite(n) ? n : (id || null);
});

// Quelle für Anzeige: lokal nachgeladene Daten oder Fallback auf übergebene Notification
const currentNotification = computed(() => localNotification.value || props.notification || {});

// Chat nur, wenn Erstellerrolle der Meldung = Eltern (roleId=3)
const creatorRoleIdOfCurrent = computed(() => {
  const n = currentNotification.value || {};
  const v = n.creatorRoleId ?? n.creator_role_id ?? null;
  const num = Number(v);
  return Number.isFinite(num) ? num : null;
});
const threadRootCreatorRoleId = ref(null);
const showChat = computed(() => creatorRoleIdOfCurrent.value === 3 || threadRootCreatorRoleId.value === 3);

// Admins immer; Lehrer nur, wenn Ersteller der Meldung
const canEditLatest = computed(() => isAdmin.value || isTeacher.value);

const isMarking = ref(false);
const actioning = ref(false);

// Dialogzustand für Pflicht-Grund
const reasonDialog = ref({
  open: false,
  mode: null, // 'akzeptiert' | 'abgelehnt'
  text: ''
});
const deleteDialog = ref({open: false});
const reasonError = computed(() => reasonDialog.value.open && !String(reasonDialog.value.text || '').trim());

function openReason(mode) {
  reasonDialog.value.mode = mode;
  reasonDialog.value.text = '';
  reasonDialog.value.open = true;
}

function cancelReason() {
  reasonDialog.value.open = false;
  reasonDialog.value.mode = null;
  reasonDialog.value.text = '';
}

async function confirmReason() {
  const txt = String(reasonDialog.value.text || '').trim();
  if (!txt) return;
  const mode = reasonDialog.value.mode === 'akzeptiert' ? 'akzeptiert' : 'abgelehnt';
  const eventName = mode === 'akzeptiert' ? 'accept' : 'reject';
  const res = await updateStatusAndClose(mode, eventName, txt);
  if (res) {
    toast.add({
      severity: 'success',
      summary: mode === 'akzeptiert' ? 'Meldung akzeptiert.' : 'Meldung abgelehnt.',
      life: 3500
    });
  }
  cancelReason();
}

// Eltern-Thread (Root -> ... -> Aktuell)
const thread = ref([]); // Array von Notifications
const activeThreadIndex = ref(0);
const isViewingLatest = computed(() => activeThreadIndex.value >= (thread.value.length - 1));

async function buildThread() {
  const start = localNotification.value || props.notification || null;
  const startId = start?.id ?? props.notificationId ?? null;
  if (!startId) {
    thread.value = [];
    activeThreadIndex.value = 0;
    return;
  }

  try {
    // Vollständigen Thread serverseitig liefern lassen
    const res = await api.get('/notifications/thread', {params: {id: startId}});
    const data = Array.isArray(res?.data) ? res.data
        : (Array.isArray(res?.data?.data) ? res.data.data
            : (Array.isArray(res?.data?.items) ? res.data.items : []));
    const items = (data || []).filter(it => it && (it.id != null));

    // Fallback: wenn Backend nichts liefert, mindestens Start anzeigen
    const chain = items.length ? items : [start];

    thread.value = chain;
    activeThreadIndex.value = chain.length ? (chain.length - 1) : 0;
    localNotification.value = chain[activeThreadIndex.value] || start;

    // Thread-Wurzel-Erstellerrolle merken (für Chat-Sichtbarkeit)
    try {
      const role = Number(chain[0]?.creatorRoleId ?? chain[0]?.creator_role_id ?? NaN);
      threadRootCreatorRoleId.value = Number.isFinite(role) ? role : null;
    } catch {
      threadRootCreatorRoleId.value = null;
    }
  } catch {
    // Im Fehlerfall wenigstens Start anzeigen
    thread.value = [start].filter(Boolean);
    activeThreadIndex.value = thread.value.length ? (thread.value.length - 1) : 0;
    localNotification.value = thread.value[activeThreadIndex.value] || start || null;
  }
}

function selectThread(idx) {
  if (!Array.isArray(thread.value) || !thread.value[idx]) return;
  localNotification.value = thread.value[idx];
  activeThreadIndex.value = idx;
}

/**
 * Lädt bei Bedarf nur die Thread-Wurzel und setzt deren Erstellerrolle,
 * ohne Tabs/Historie für Eltern anzuzeigen.
 */
async function ensureRootCreatorRoleLoaded() {
  // Bereits bekannt → nichts tun
  if (threadRootCreatorRoleId.value === 3) return;
  const id = currentId.value;
  if (!props.open || !id) return;
  try {
    const res = await api.get('/notifications/thread', {params: {id}});
    const data = Array.isArray(res?.data) ? res.data
        : (Array.isArray(res?.data?.data) ? res.data.data
            : (Array.isArray(res?.data?.items) ? res.data.items : []));
    const root = (data || [])[0] || null;
    const role = Number(root?.creatorRoleId ?? root?.creator_role_id ?? NaN);
    threadRootCreatorRoleId.value = Number.isFinite(role) ? role : null;
  } catch {
    // still
  }
}

async function updateStatusAndClose(nextStatus, eventName, reasonText) {
  const id = currentId.value;
  if (!id || actioning.value) return null;
  actioning.value = true;
  try {
    const payload = {status: nextStatus};
    if (typeof reasonText === 'string' && reasonText.trim()) {
      payload.reason = reasonText.trim();
    }
    const res = await api.post(`/notifications/${encodeURIComponent(id)}/status`, payload);
    const updated = res?.data ?? {id, status: nextStatus};
    // lokalen Status synchronisieren, falls vorhanden
    if (localNotification.value && updated?.status) {
      localNotification.value.status = updated.status;
      localNotification.value.updatedAt = updated.updatedAt ?? new Date().toISOString();
    }
    emit(eventName, updated);
    // Dialog schließen; Parent lädt Liste beim Close
    emit('close');
    return updated;
  } catch {
    // optional: Fehlerbehandlung (Toast/Notice) könnte ergänzt werden
    return null;
  } finally {
    actioning.value = false;
  }
}

// PrimeVue Toast
const toast = useToast();

async function onCreateCorrection() {
  // Viewer schließen, bevor die Korrektur gestartet wird
  try {
    closeViewer();
  } catch { /* ignore */
  }
  // Auf den nächsten DOM-Zyklus warten, damit das Overlay sicher entfernt ist
  await nextTick();
  // Korrekturen werden am Thread-Anker gestartet; Fallback ist die aktuell sichtbare Meldung.
  const data = Array.isArray(thread.value) && thread.value.length
      ? (thread.value[0] || currentNotification.value || {})
      : (currentNotification.value || {});
  emit('create-correction', data);
}

/**
 * Erstellt das Entschuldigungs-PDF (A4, mm) und liefert { base64, fileName }.
 */
function buildExcusePdf() {
  const n = currentNotification.value || {};

  // Name in der Form "Nachname, Vorname" aufbauen
  const rawFirst =
      n.studentFirstName ?? n.student_first_name ?? n.firstName ?? n.first_name ?? '';
  const rawLast =
      n.studentLastName ?? n.student_last_name ?? n.lastName ?? n.last_name ?? '';
  let firstName = String(rawFirst || '').trim();
  let lastName = String(rawLast || '').trim();
  if (!firstName || !lastName) {
    const s = String(studentName.value || '').trim();
    if (s.includes(',')) {
      const parts = s.split(',').map(p => p.trim());
      lastName = lastName || (parts[0] || '');
      firstName = firstName || (parts[1] || '');
    } else if (s) {
      const parts = s.split(/\s+/);
      if (parts.length >= 2) {
        firstName = firstName || parts.slice(0, -1).join(' ');
        lastName = lastName || parts.slice(-1)[0];
      } else {
        firstName = firstName || s;
      }
    }
  }
  const nameOrdered = [lastName, firstName].filter(Boolean).join(', ') || (studentName.value || '—');

  const group =
      n.groupName ?? n.studentGroup ?? n.className ?? n.class ?? n.group ?? '';
  const from = n.dateFrom ?? n.date_from ?? null;
  const to = n.dateTo ?? n.date_to ?? null;
  const tFrom = n.timeFrom ?? n.time_from ?? null;
  const tTo = n.timeTo ?? n.time_to ?? null;

  const isHours = Boolean(from && to && isSameYMD(from, to) && (tFrom || tTo));
  const isMulti = Boolean(from && to && !isSameYMD(from, to));
  const isFull = Boolean(
      (from && to && isSameYMD(from, to) && !isHours) ||
      (from && !to && !tFrom && !tTo)
  );

  const subjText = String(subjectText.value || '').trim();
  const commentText = String(message.value || '').trim();
  const dateText = dateRange.value || '—';

  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  const todayText = `${dd}.${mm}.${yyyy}`;

  const doc = new jsPDF({unit: 'mm', format: 'a4'});
  const left = 20;
  let y = 22;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('Antrag auf Entschuldigung', left, y);
  y += 10;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');

  // Name, Vorname (Ausgabe: Nachname, Vorname)
  doc.text('Name, Vorname:', left, y);
  doc.setFont('helvetica', 'bold');
  doc.text(String(nameOrdered), left + 45, y);
  doc.setFont('helvetica', 'normal');
  y += 10;

  // Lerngruppe
  doc.text('Lerngruppe:', left, y);
  doc.setFont('helvetica', 'bold');
  doc.text(String(group || '—'), left + 45, y);
  doc.setFont('helvetica', 'normal');
  y += 10;

  // Datum der Fehlzeit
  doc.text('Datum der Fehlzeit:', left, y);
  doc.setFont('helvetica', 'bold');
  doc.text(String(dateText), left + 45, y);
  doc.setFont('helvetica', 'normal');
  y += 14;

  // Auswahlfelder
  const box = (x, yy, checked, label) => {
    const s = 5;
    doc.rect(x, yy - 4.5, s, s);
    if (checked) {
      doc.setFont('helvetica', 'bold');
      doc.text('X', x + 1.6, yy - 0.8);
      doc.setFont('helvetica', 'normal');
    }
    doc.text(label, x + s + 3, yy);
  };
  box(left, y, isFull, 'Ganztägig');
  box(left + 50, y, isMulti, 'Mehrtägig');
  box(left + 105, y, isHours, 'Stundenweise');
  y += 16;

  // Grund
  doc.setFont('helvetica', 'normal');
  doc.text('Grund:', left, y);

  const maxWidth = 170;
  const labelOffset = 45; // rechter Bereich neben "Grund:"
  const lineHeight = 6;

  // Subject fett rechts neben "Grund:"
  const subjectLines = subjText ? doc.splitTextToSize(subjText, maxWidth - labelOffset) : [];
  if (subjectLines.length) {
    doc.setFont('helvetica', 'bold');
    doc.text(subjectLines, left + labelOffset, y);
    doc.setFont('helvetica', 'normal');
  }
  const subjBlockHeight = (subjectLines.length ? subjectLines.length : 1) * lineHeight;
  y += subjBlockHeight + 2;

  // Kommentar in kleinerer Schrift darunter
  if (commentText) {
    doc.setFontSize(10);
    const commentLines = doc.splitTextToSize(commentText, maxWidth - labelOffset);
    doc.text(commentLines, left + labelOffset, y);
    y += Math.max(1, commentLines.length) * (lineHeight - 0); // fortlaufend unter dem Subject
    doc.setFontSize(12);
  } else {
    y += 6;
  }

  // etwas Abstand nach dem Block
  y += 8;

  // Ort, Datum / Unterschrift
  doc.text('Ort, Datum:', left, y);
  doc.setFont('helvetica', 'bold');
  doc.text(todayText, left + 30, y);
  doc.setFont('helvetica', 'normal');

  // Unterschrift-Linie rechts
  const signX = left + 95;
  doc.text('Unterschrift:', signX, y);
  doc.setDrawColor(224, 227, 234);
  doc.line(signX + 28, y + 1, left + maxWidth, y + 1);

  // Hinweis (kursiv) unter der Unterschriftszeile
  y += 8;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(10);
  doc.text('Dieses Schreiben wurde maschinell erstellt und ist ohne Unterschrift gültig.', left, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);

  // Dateiname
  const fileName =
      `Entschuldigung_${String(nameOrdered).replace(/\s+/g, '_')}_${yyyy}-${mm}-${dd}.pdf`;

  // Base64 extrahieren
  const dataUri = doc.output('datauristring'); // data:application/pdf;base64,....
  const base64 = String(dataUri).split(',')[1] || '';

  return {base64, fileName};
}

/**
 * Erzeugt die Entschuldigungs-PDF, fügt sie temporär als Attachment ein
 * und öffnet sie direkt im Viewer.
 */
async function onGenerateExcusePdf() {
  try {
    const {base64, fileName} = buildExcusePdf();

    const tempAtt = {
      id: null,
      url: null,
      base64Url: null,
      name: fileName,
      kind: 'file',
      mime: 'application/pdf',
      previewUrl: null,
      _base64: base64,
      _temp: true
    };

    attachments.value.push(tempAtt);
    await nextTick();
    await openAttachment(tempAtt);
  } catch {
    try {
      toast.add({severity: 'danger', summary: 'PDF konnte nicht erstellt werden.', life: 3000});
    } catch {
      // ignore
    }
  }
}

async function markAsRead() {
  // Guard: nur bei geöffnetem Dialog, vorhandener ID und Status 'offen'
  const id = currentId.value;
  const current = currentNotification.value?.status;
  if (!props.open || !id || isMarking.value || current !== 'offen' || isParent.value) return;
  isMarking.value = true;
  try {
    const res = await api.post(`/notifications/${encodeURIComponent(id)}/status`, {status: 'gelesen'});
    const updated = res?.data ?? null;
    // lokalen Status synchronisieren
    if (localNotification.value) {
      localNotification.value.status = 'gelesen';
      localNotification.value.updatedAt = new Date().toISOString();
    }
    // Parent informieren, damit Liste/Selektion aktualisiert werden kann
    emit('read', updated ?? {id, status: 'gelesen'});
  } catch {
    // still: Fehler hier nicht fatal; UI bleibt geöffnet
  } finally {
    isMarking.value = false;
  }
}

// Details laden (falls nötig), dann lesen markieren und Anhänge laden
async function loadNotificationDetails(id) {
  if (!id) return;
  localLoading.value = true;
  try {
    // Versuch: Server unterstützt Filter per id
    const res = await api.get('/notifications', {params: {id}});
    const raw = res?.data;
    const list = Array.isArray(raw) ? raw
        : Array.isArray(raw?.items) ? raw.items
            : (raw && typeof raw === 'object' && raw.id ? [raw] : []);
    const found = list.find(x => String(x?.id) === String(id)) || list[0] || null;
    if (found) {
      localNotification.value = found;
    }
  } catch {
    // Fallback: Alles laden und clientseitig finden (nur falls notwendig)
    try {
      const resAll = await api.get('/notifications');
      const listAll = Array.isArray(resAll?.data) ? resAll.data : (Array.isArray(resAll?.data?.items) ? resAll.data.items : []);
      const found = listAll.find(x => String(x?.id) === String(id)) || null;
      if (found) localNotification.value = found;
    } catch {
      // still: keine harten Fehler werfen
    }
  } finally {
    localLoading.value = false;
  }
}

function needsFetchFor(obj) {
  const o = obj || {};
  const required = ['studentName', 'sentAt', 'status', 'comment'];
  // wenn keine ID -> nichts zu tun (Dialog zeigt dann Platzhalter)
  if (!currentId.value) return false;
  // wenn lokale Daten vorhanden -> nicht erneut
  if (localNotification.value && String(localNotification.value.id) === String(currentId.value)) return false;
  // wenn gar kein Objekt übergeben -> laden
  if (!obj || typeof obj !== 'object' || Object.keys(obj).length <= 1) return true;
  // wenn Kerndaten fehlen -> laden
  return required.some(k => typeof o[k] === 'undefined' && typeof o[k.replace(/([A-Z])/g, '_$1').toLowerCase()] === 'undefined');
}

async function fetchMyPermissions() {
  // Admins benötigen keine spezifischen Permissions zur Moderation
  if (!isTeacher.value || !userId.value) {
    myPermissionKeys.value = [];
    return;
  }
  try {
    const res = await api.get(`/users/${encodeURIComponent(userId.value)}/permissions`);
    const arr = Array.isArray(res?.data) ? res.data
        : (Array.isArray(res?.data?.data) ? res.data.data : []);
    myPermissionKeys.value = (arr || [])
        .map(p => (p?.key_name ?? p?.keyName ?? p?.key ?? ''))
        .map(s => String(s || '').trim().toLowerCase())
        .filter(Boolean);
  } catch {
    myPermissionKeys.value = [];
  }
}

watch(() => props.open, async (o) => {
  if (o) {
    // Eigene Permissions für Lehrer laden (Admin nicht nötig)
    await fetchMyPermissions();

    if (needsFetchFor(props.notification)) {
      await loadNotificationDetails(currentId.value);
    }
    // Thread nur für Admins/Lehrer aufbauen; Eltern sehen nur die aktuelle Meldung
    if (isAdmin.value || isTeacher.value) {
      await buildThread();
    } else {
      // Eltern: keine Historie
      thread.value = [];
      activeThreadIndex.value = 0;
      // sicherstellen, dass die aktuelle Meldung gesetzt ist
      if (!localNotification.value) {
        localNotification.value = props.notification || {};
      }
      // Root-Erstellerrolle für Chat ermitteln (ohne Tabs anzuzeigen)
      await ensureRootCreatorRoleLoaded();
    }

    await markAsRead();

    // Chat NICHT hier laden -> wird zentral im currentId-Watcher geladen,
    // sonst entstehen doppelte Requests (/chat und /chat/read).
    chatMessages.value = [];
    unreadChatCount.value = 0;

    // Hinweis: fetchAttachments() / fetchReasons() NICHT mehr hier aufrufen.
    // Der currentId-Watcher übernimmt das, sobald die endgültige ID feststeht.
  } else {
    clearAttachments();
    localNotification.value = null;
    thread.value = [];
    activeThreadIndex.value = 0;
    chatMessages.value = [];
    unreadChatCount.value = 0;
  }
});

function parseDateLike(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(?:$|T)/.test(value)) {
    // YYYY-MM-DD (vermeide TZ-Shift)
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function fmtDate(d) {
  const dt = parseDateLike(d);
  if (!dt) return '-';
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yyyy = dt.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function fmtTime(v) {
  if (!v) return '';
  // Bereits als HH:MM(:SS) angegeben → direkt anzeigen
  if (typeof v === 'string' && /^(\d{2}):(\d{2})(?::\d{2})?$/.test(v)) {
    const m = v.match(/^(\d{2}):(\d{2})/);
    return m ? `${m[1]}:${m[2]}` : '';
  }
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const tf = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return tf.format(d);
}

function fmtDateTime(v) {
  const val = v;
  let dt = null;

  if (val instanceof Date) {
    dt = val;
  } else if (typeof val === 'number') {
    dt = new Date(val > 1e12 ? val : val * 1000);
  } else if (typeof val === 'string') {
    const tmp = new Date(val.trim());
    dt = Number.isNaN(tmp.getTime()) ? null : tmp;
  }

  if (!dt || Number.isNaN(dt.getTime())) return '-';

  const df = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const tf = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  return `${df.format(dt)} ${tf.format(dt)}`;
}

function isSameYMD(a, b) {
  const da = parseDateLike(a);
  const db = parseDateLike(b);
  if (!da || !db) return false;
  return da.getFullYear() === db.getFullYear()
      && da.getMonth() === db.getMonth()
      && da.getDate() === db.getDate();
}

const dateRange = computed(() => {
  const n = currentNotification.value || {};
  const from = n.dateFrom ?? n.date_from ?? null;
  const to = n.dateTo ?? n.date_to ?? null;
  const tFrom = n.timeFrom ?? n.time_from ?? null;
  const tTo = n.timeTo ?? n.time_to ?? null;

  // zeitweise: Datum gleich + Zeiten vorhanden
  if (from && to && isSameYMD(from, to) && (tFrom || tTo)) {
    const date = fmtDate(from);
    const tf = fmtTime(tFrom);
    const tt = fmtTime(tTo);
    if (tf && tt) return `${date} ${tf}–${tt}`;
    if (tf) return `${date} ab ${tf}`;
    if (tt) return `${date} bis ${tt}`;
    return date;
  }

  // tageweise
  if (from && to) {
    if (isSameYMD(from, to)) return fmtDate(from);
    return `${fmtDate(from)} — ${fmtDate(to)}`;
  }
  if (from) return fmtDate(from);
  if (to) return fmtDate(to);
  return '—';
});

const studentName = computed(() =>
    currentNotification.value?.studentName ?? currentNotification.value?.student_name ?? '—'
);

// Absender: bevorzugt first_name + last_name, sonst Fallbacks (reine IDs ausblenden)
const sender = computed(() => {
  const n = currentNotification.value || {};
  const first = n.parentFirstName ?? n.parent_first_name ?? '';
  const last = n.parentLastName ?? n.parent_last_name ?? '';
  const full = `${String(first).trim()} ${String(last).trim()}`.trim().replace(/\s+/g, ' ');
  if (full) return full;

  const fb =
      n.parentName ??
      n.parent_name ??
      n.sender?.username ??
      n.senderName ??
      '';
  const s = String(fb ?? '').trim();
  return /^\d+$/.test(s) ? '—' : (s || '—');
});

const sentAtFormatted = computed(() =>
    fmtDateTime(
        currentNotification.value?.sentAt ??
        currentNotification.value?.sent_at ??
        currentNotification.value?.createdAt ??
        currentNotification.value?.created_at ??
        null
    )
);

const createdAtOnlyFormatted = computed(() =>
    fmtDate(
        currentNotification.value?.createdAt ??
        currentNotification.value?.created_at ??
        null
    )
);

const statusLabel = computed(() => {
  const n = currentNotification.value || {};
  const raw = (n.status ?? n.state ?? '').toString().toLowerCase().trim();
  const allowed = ['offen', 'gelesen', 'abgelehnt', 'akzeptiert'];
  if (allowed.includes(raw)) {
    // Erstes Zeichen groß für Anzeige
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }
  // Fallback: alternatives Feld state falls vorhanden, sonst Dash
  const alt = (n.state ?? '').toString().trim();
  return alt || '—';
});

const updatedAtFormatted = computed(() =>
    fmtDateTime(
        currentNotification.value?.updatedAt ??
        currentNotification.value?.updated_at ??
        null
    )
);

// Grund/Subject für Header
const subjectText = computed(() => {
  const n = currentNotification.value || {};
  const s = n.subject ?? n.subject_text ?? null;
  return typeof s === 'string' ? s.trim() : '';
});

// Kommentar (statt früherem Subject an der Anzeige-Stelle)
const message = computed(() =>
    (currentNotification.value?.comment ?? currentNotification.value?.comment_text ?? currentNotification.value?.message ?? currentNotification.value?.text ?? currentNotification.value?.body ?? '')
);

const attachments = ref([]);
const reasons = ref([]);
const reasonsLoading = ref(false);

// Chat-State
const chatMessages = ref([]);
const chatLoading = ref(false);
const chatInput = ref('');
const sending = ref(false);
const unreadChatCount = ref(0);

async function fetchChat() {
  const id = currentId.value;
  if (!props.open || !id) {
    chatMessages.value = [];
    unreadChatCount.value = 0;
    return;
  }
  chatLoading.value = true;
  try {
    const res = await api.get(`/notifications/${encodeURIComponent(id)}/chat`);
    const data = Array.isArray(res?.data) ? res.data
        : (Array.isArray(res?.data?.data) ? res.data.data : []);
    chatMessages.value = (data || []).map((m) => ({
      id: m.id ?? null,
      threadId: m.threadId ?? m.thread_id ?? null,
      senderUserId: m.senderUserId ?? m.sender_user_id ?? null,
      senderName: m.senderName ?? m.sender_name ?? m.username ?? '',
      message: m.message ?? '',
      createdAt: m.createdAt ?? m.created_at ?? null
    }));
    // Nach Laden gleich als gelesen markieren
    await markChatRead();
    unreadChatCount.value = 0;
  } catch {
    chatMessages.value = [];
  } finally {
    chatLoading.value = false;
  }
}

async function markChatRead() {
  const id = currentId.value;
  if (!props.open || !id) return;
  try {
    await api.post(`/notifications/${encodeURIComponent(id)}/chat/read`, {});
  } catch {
    // ignore
  }
}

async function sendChat() {
  const id = currentId.value;
  const text = String(chatInput.value || '').trim();
  if (!id || !text || sending.value) return;
  sending.value = true;
  try {
    const res = await api.post(`/notifications/${encodeURIComponent(id)}/chat`, {message: text});
    const m = res?.data || null;
    if (m) {
      chatMessages.value.push({
        id: m.id ?? null,
        threadId: m.threadId ?? m.thread_id ?? null,
        senderUserId: m.senderUserId ?? m.sender_user_id ?? userId.value ?? null,
        senderName: m.senderName ?? m.sender_name ?? (auth.user?.displayName || auth.user?.display_name || 'Ich'),
        message: m.message ?? text,
        createdAt: m.createdAt ?? m.created_at ?? new Date().toISOString()
      });
      chatInput.value = '';
    } else {
      // Fallback: lokalen Echo-Eintrag
      chatMessages.value.push({
        id: null,
        threadId: null,
        senderUserId: userId.value ?? null,
        senderName: auth.user?.displayName || auth.user?.display_name || 'Ich',
        message: text,
        createdAt: new Date().toISOString()
      });
      chatInput.value = '';
    }
    await markChatRead();
  } catch {
    // optional: Toast
    try {
      toast.add({severity: 'danger', summary: 'Nachricht konnte nicht gesendet werden.', life: 3000});
    } catch {/* ignore */
    }
  } finally {
    sending.value = false;
  }
}

async function fetchReasons() {
  const id = currentId.value;
  if (!props.open || !id) {
    reasons.value = [];
    return;
  }
  reasonsLoading.value = true;
  try {
    const res = await api.get(`/notifications/${encodeURIComponent(id)}/reasons`);
    const data = Array.isArray(res?.data) ? res.data
        : (Array.isArray(res?.data?.items) ? res.data.items : []);
    reasons.value = (data || []).map((r) => {
      const userName =
          (typeof r.userName === 'string' && r.userName.trim()) ? r.userName
              : (typeof r.username === 'string' && r.username.trim()) ? r.username
                  : '';
      return {
        id: r.id ?? null,
        userId: r.userId ?? r.user_id ?? null,
        userName,
        status: r.status ?? '',
        reason: r.reason ?? '',
        createdAt: r.createdAt ?? r.created_at ?? null
      };
    });
  } catch {
    reasons.value = [];
  } finally {
    reasonsLoading.value = false;
  }
}

// Galerie/Viewer-Status
const viewer = ref({open: false, index: 0});
const viewerLoading = ref(false);
const currentObjectUrl = ref(null); // für PDFs (Blob-URL)

/**
 * Blob-URLs der Previews aufräumen und Liste leeren
 */
function clearAttachments() {
  try {
    attachments.value.forEach(a => {
      if (a && a.previewUrl) {
        URL.revokeObjectURL(a.previewUrl);
      }
    });
  } catch {
    // ignore
  }
  attachments.value = [];
}

function hasAttachmentFlag(n) {
  if (!n || typeof n !== 'object') return false;
  const v = n.hasAttachment ?? n.has_attachments ?? n.has_attachment ?? null;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v > 0;
  const c = n.attachmentsCount ?? n.attachments_count ?? n.attachmentCount ?? null;
  if (typeof c === 'number') return c > 0;
  return Boolean(v);
}

/**
 * Lädt die Anhänge-Metadaten und erzeugt bei Bildern eine Preview (Blob-URL).
 */
async function fetchAttachments() {
  const id = currentId.value;
  const should = hasAttachmentFlag(currentNotification.value);
  // Nur laden, wenn Dialog offen ist, eine ID vorhanden ist und laut Notification Anhänge existieren
  if (!props.open || !id || !should) {
    clearAttachments();
    return;
  }
  try {
    const res = await api.get(`/notifications/${encodeURIComponent(id)}/attachments`);
    const data = Array.isArray(res?.data) ? res.data : (res?.data?.attachments ?? []);

    // Map API-Daten auf UI-Objekte, URL für axios-Instanz relativ herstellen
    attachments.value = (data || [])
        .map((d) => {
          const rawUrl = d?.url || d?.href || null;
          const apiPath = typeof rawUrl === 'string' ? rawUrl.replace(/^\/api\//, '/') : null;
          const name = d?.name || d?.filename || 'Anhang';
          const mime = d?.mimeType || d?.mime_type || '';
          const isImage = String(mime).startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
          const kind = isImage ? 'image' : 'file';
          const normalizedMime = isImage
              ? (String(mime).startsWith('image/') ? mime
                  : (name.toLowerCase().endsWith('.png') ? 'image/png'
                      : name.toLowerCase().endsWith('.jpg') || name.toLowerCase().endsWith('.jpeg') ? 'image/jpeg'
                          : name.toLowerCase().endsWith('.gif') ? 'image/gif'
                              : name.toLowerCase().endsWith('.webp') ? 'image/webp'
                                  : name.toLowerCase().endsWith('.bmp') ? 'image/bmp'
                                      : name.toLowerCase().endsWith('.svg') ? 'image/svg+xml'
                                          : 'image/*'))
              : (mime || null);
          // Base64-Endpoint vorbereiten (für Previews/Öffnen)
          const base64Url = apiPath ? `${apiPath}/base64` : null;
          return apiPath ? {
            id: d?.id,
            url: apiPath,
            base64Url,
            name,
            kind,
            mime: normalizedMime,
            previewUrl: null
          } : null;
        })
        .filter(Boolean);

    // Für Bildanhänge Preview als Data-URL laden
    const previews = attachments.value
        .map((a, idx) => ({a, idx}))
        .filter(x => x.a.kind === 'image' && x.a.base64Url);

    await Promise.allSettled(
        previews.map(async ({a}) => {
          try {
            const resImg = await api.get(a.base64Url, {responseType: 'json'});
            const b64 = resImg?.data?.base64 || '';
            const mt = a.mime && String(a.mime).startsWith('image/') ? a.mime : (resImg?.data?.mimeType || 'image/*');
            const fname = resImg?.data?.filename || a.name || 'Anhang';
            if (!b64) throw new Error('empty');
            // Für Preview:
            a.previewUrl = `data:${mt};base64,${b64}`;
            // WICHTIG: Base64 zwischenspeichern, damit ensureBase64 nicht erneut lädt
            a._base64 = b64;
            a.mime = mt || a.mime;
            a.name = fname || a.name;
          } catch {
            a.previewUrl = null;
          }
        })
    );
  } catch {
    // Im Fehlerfall leeren wir die Liste still
    clearAttachments();
  }
}

// Bei ID-Wechsel (während offen) Details und Anhänge neu laden
watch(currentId, async (n) => {
  if (n && props.open) {
    if (needsFetchFor(currentNotification.value)) {
      await loadNotificationDetails(n);
    }
    await fetchAttachments();
    if (isAdmin.value || isTeacher.value) {
      await fetchReasons();
    }

    // Root-Erstellerrolle IMMER nachladen, bevor showChat ausgewertet wird.
    // Sonst verschwindet der Chat bei Children (z.B. Korrektur), weil das Child selbst creatorRoleId=1/2 hat.
    await ensureRootCreatorRoleLoaded();

    if (showChat.value) {
      await fetchChat();
    } else {
      chatMessages.value = [];
      unreadChatCount.value = 0;
    }
  }
});

/**
 * Öffnet die Galerie und lädt die gewählte Datei (Base64).
 */
async function openAttachment(att) {
  if (!att) return;
  const idx = attachments.value.findIndex(a => a === att);
  viewer.value.index = idx >= 0 ? idx : 0;
  viewer.value.open = true;
  await loadCurrent();
}

/* Berechnete Eigenschaften für aktuellen Viewer-Eintrag */
const currentAttachment = computed(() => attachments.value[viewer.value.index] || null);
const currentFileName = computed(() => currentAttachment.value?.name || 'Datei');
const isCurrentImage = computed(() => {
  const m = currentAttachment.value?.mime || '';
  if (m) return /^image\//.test(String(m));
  // Fallback über kind
  return currentAttachment.value?.kind === 'image';
});
const isCurrentPdf = computed(() => {
  const m = (currentAttachment.value?.mime || '').toLowerCase();
  return m === 'application/pdf' || /\.pdf$/i.test(currentFileName.value);
});
const currentDataUrl = computed(() => {
  const att = currentAttachment.value;
  if (!att || !att._base64) return null;
  const mime = att.mime || 'application/octet-stream';
  return `data:${mime};base64,${att._base64}`;
});
const canPrint = computed(() => isCurrentImage.value || isCurrentPdf.value);

/* Base64 laden und für Anzeige vorbereiten */
async function ensureBase64(att) {
  if (!att || att._base64) return;
  if (!att.base64Url) throw new Error('Kein Base64-Endpunkt vorhanden');
  const res = await api.get(att.base64Url, {responseType: 'json'});
  const data = res?.data || {};
  const b64 = data.base64;
  const mime = data.mimeType || att.mime || 'application/octet-stream';
  const fname = data.filename || att.name || 'Datei';
  if (!b64) throw new Error('Kein Inhalt erhalten');
  att._base64 = b64;
  att.mime = mime;
  att.name = fname;
}

function base64ToBlob(b64, mime) {
  const byteString = atob(b64);
  const len = byteString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = byteString.charCodeAt(i);
  return new Blob([bytes], {type: mime || 'application/octet-stream'});
}

async function loadCurrent() {
  const att = currentAttachment.value;
  if (!att) return;
  // Vorherige ObjectURL freigeben
  if (currentObjectUrl.value) {
    try {
      URL.revokeObjectURL(currentObjectUrl.value);
    } catch {
      // ignore
    }
    currentObjectUrl.value = null;
  }
  viewerLoading.value = true;
  try {
    await ensureBase64(att);
    if (isCurrentPdf.value && att._base64) {
      const blob = base64ToBlob(att._base64, att.mime || 'application/pdf');
      currentObjectUrl.value = URL.createObjectURL(blob);
    }
  } catch {
    try {
      toast.add({severity: 'danger', summary: 'Datei konnte nicht geladen werden.', life: 3000});
    } catch {
      // ignore
    }
  } finally {
    viewerLoading.value = false;
  }
}

function closeViewer() {
  viewer.value.open = false;
  if (currentObjectUrl.value) {
    try {
      URL.revokeObjectURL(currentObjectUrl.value);
    } catch {
      // ignore
    }
    currentObjectUrl.value = null;
  }
}

async function nextInViewer() {
  if (attachments.value.length <= 1) return;
  viewer.value.index = (viewer.value.index + 1) % attachments.value.length;
  await loadCurrent();
}

async function prevInViewer() {
  if (attachments.value.length <= 1) return;
  viewer.value.index = (viewer.value.index - 1 + attachments.value.length) % attachments.value.length;
  await loadCurrent();
}

async function downloadCurrent() {
  const att = currentAttachment.value;
  if (!att) return;
  try {
    await ensureBase64(att);
    const blob = base64ToBlob(att._base64, att.mime);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.name || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
  } catch {
    try {
      toast.add({severity: 'danger', summary: 'Download fehlgeschlagen.', life: 3000});
    } catch {
      // ignore
    }
  }
}

async function printCurrent() {
  const att = currentAttachment.value;
  if (!att || !canPrint.value) return;
  try {
    await ensureBase64(att);

    if (isCurrentImage.value) {
      const dataUrl = `data:${att.mime || 'image/*'};base64,${att._base64}`;
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);
      const html = `
<!doctype html>
<html>
<head><title>${(att.name || 'Bild').replace(/</g, '&lt;')}</title>
<style>html,body{margin:0;padding:0} img{max-width:100vw;max-height:100vh;}</style>
</head>
<body><img src="${dataUrl}" alt=""></body>
</html>`;
      iframe.srcdoc = html;
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch {
          // ignore
        }
        setTimeout(() => iframe.remove(), 2000);
      };
    } else if (isCurrentPdf.value) {
      const blob = base64ToBlob(att._base64, 'application/pdf');
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);
      iframe.src = url;
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch {
          // ignore
        }
        setTimeout(() => {
          try {
            URL.revokeObjectURL(url);
          } catch {
            // ignore
          }
          iframe.remove();
        }, 5 * 60 * 1000);
      };
    }
  } catch {
    try {
      toast.add({severity: 'danger', summary: 'Drucken nicht möglich.', life: 3000});
    } catch {
      // ignore
    }
  }
}

async function onDelete() {
  if (!(isAdmin.value || isTeacher.value) || actioning.value) return;
  deleteDialog.value.open = true;
}

function cancelDelete() {
  deleteDialog.value.open = false;
}

async function confirmDelete() {
  const id = currentId.value;
  if (!(isAdmin.value || isTeacher.value) || !id || actioning.value) return;
  actioning.value = true;
  try {
    await api.delete(`/notifications/${encodeURIComponent(id)}`);
    try {
      toast.add({severity: 'success', summary: 'Meldung gelöscht.', life: 3500});
    } catch {
      // ignore
    }
    deleteDialog.value.open = false;
    // Dialog schließen -> Parent lädt Liste neu
    emit('close');
  } catch {
    try {
      toast.add({severity: 'danger', summary: 'Löschen fehlgeschlagen.', life: 3000});
    } catch {
      // ignore
    }
  } finally {
    actioning.value = false;
  }
}
</script>

<style scoped>
.content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.meta {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--surface);
}

.meta-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px;
  align-items: center;
}

.label {
  color: var(--muted);
  font-size: 12px;
}

.value {
  color: var(--text);
  font-size: 14px;
}

.message {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
}

/* Meta + Gründe (Status-Historie) nebeneinander */
.meta-reasons-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 12px;
  align-items: start;
}

.reasons-card {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--surface);
}

.reasons-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 6px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--border);
}

.reasons-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  margin: 0;
}

.reasons-muted {
  color: var(--muted);
  font-size: 12px;
}

.reasons-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.reasons-item {
  display: grid;
  gap: 4px;
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
}

.reasons-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.reasons-line {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 8px;
  align-items: center;
}

.reasons-user {
  color: var(--text);
  font-size: 14px;
}

.reasons-time {
  color: var(--muted);
  font-size: 12px;
}

.reason-text {
  margin: 0;
  color: var(--text);
  font-size: 13px;
  line-height: 1.4;
}

/* Badge-Stile */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  color: var(--text);
  background: var(--bg);
  border: 1px solid var(--border);
}

.badge-success {
  background: color-mix(in srgb, var(--success) 15%, #fff 85%);
  border-color: color-mix(in srgb, var(--success) 40%, #fff 60%);
  color: #065f46;
}

.badge-danger {
  background: color-mix(in srgb, var(--danger) 15%, #fff 85%);
  border-color: color-mix(in srgb, var(--danger) 40%, #fff 60%);
  color: #7f1d1d;
}

@media (max-width: 768px) {
  .meta-reasons-grid {
    grid-template-columns: 1fr;
  }
}

/* Tabs für Eltern-Thread */
.tabs {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 4px;
}

.tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  font-size: 12px;
}

.tab:hover {
  background: color-mix(in srgb, var(--primary) 8%, transparent);
}

.tab.active {
  border-color: rgba(79, 70, 229, .4);
  background: color-mix(in srgb, var(--primary) 12%, transparent);
}

.tab-title {
  font-weight: 600;
}

.tab-meta {
  color: var(--muted);
}

.thumb-strip {
  margin-top: 12px;
  border-top: 1px solid var(--border);
  padding-top: 10px;
  display: flex;
  gap: 8px;
  overflow-x: auto;
}

.thumb {
  flex: 0 0 auto;
  width: 120px;
  height: 84px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.thumb:focus-visible {
  outline: 3px solid var(--focus);
  outline-offset: 2px;
}

.thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.thumb-doc {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: var(--muted);
  padding: 6px;
  text-align: center;
}

.thumb-doc .name {
  font-size: 12px;
  line-height: 1.2;
  word-break: break-all;
  color: var(--text);
}

.footer-actions {
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.footer-left,
.footer-right {
  display: inline-flex;
  gap: 8px;
  align-items: center;
}

.footer-right {
  margin-left: auto;
  justify-content: flex-end;
}

/* Warn-Variante */
.btn-warn {
  background: var(--warn);
  color: #fff;
}

.btn-warn:hover {
  background: color-mix(in srgb, var(--warn) 85%, black 15%);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Button Group für Ablehnen/Akzeptieren */
.btn-group {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  background: var(--surface);
}

.btn-group .btn {
  height: 38px;
  border: 0;
  border-right: 1px solid var(--border);
  border-radius: 0;
  padding: 0 12px;
}

.btn-group .btn:last-child {
  border-right: 0;
}

/* Farbvarianten (Token-basiert) */
.btn-danger {
  background: var(--danger);
  color: #fff;
}

.btn-danger:hover {
  background: color-mix(in srgb, var(--danger) 85%, black 15%);
}

.btn-success {
  background: var(--success);
  color: #fff;
}

.btn-success:hover {
  background: color-mix(in srgb, var(--success) 85%, black 15%);
}

/* Galerie/Viewer */
.viewer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

/* Mini-Dialog für Grund */
.reason-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 30;
}

.reason-dialog {
  width: min(100%, 520px);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .15);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.reason-title {
  font-size: 16px;
  line-height: 1.4;
  font-weight: 500;
  color: var(--text);
  margin: 0;
}

.input {
  width: 100%;
  min-height: 90px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
  resize: vertical;
}

.input:focus {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

.help {
  font-size: 12px;
  line-height: 1.3;
  color: var(--muted);
  margin: 4px 0 0;
}

.help.danger {
  color: var(--danger);
}

.viewer {
  width: min(100%, 960px);
  max-height: 90vh;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}

.viewer-title {
  font-size: 14px;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.viewer-actions {
  display: inline-flex;
  gap: 8px;
}

.viewer-body {
  position: relative;
  background: var(--bg);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  height: 70vh;
  padding: 12px;
}

.viewer-loading {
  color: var(--muted);
  font-size: 14px;
}

.viewer-media {
  max-width: 100%;
  max-height: 100%;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .06);
}

.viewer-frame {
  width: 100%;
  height: 100%;
  border: 0;
  background: var(--surface);
  border-radius: 6px;
}

.viewer-fallback {
  color: var(--muted);
  font-size: 14px;
  text-align: center;
}

/* Chat */
.chat-card {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--surface);
  display: grid;
  gap: 8px;
  margin-top: 8px;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chat-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
}

.chat-list {
  max-height: 260px;
  overflow: auto;
  padding: 3px 10px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 10px;
}

.chat-item {
  display: flex;
  margin: 8px 0;
  align-items: flex-end;
  width: auto;
}

.chat-item.me {
  justify-content: flex-end;
}

.chat-item .bubble {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px;
  background: var(--surface);
  max-width: 68%;
}

.chat-item.me .bubble {
  background: color-mix(in srgb, var(--primary) 8%, white);
  border-color: color-mix(in srgb, var(--primary) 40%, var(--border));
}

.chat-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--muted);
}

.chat-text {
  margin: 0;
  font-size: 15px;
  color: var(--text);
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 20px;
}

.chat-input-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
}

.chat-input-row .input {
  min-height: auto;
}

.nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 44px;
  width: 44px;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.nav:hover {
  background: color-mix(in srgb, var(--primary) 8%, transparent);
}

.nav:disabled {
  opacity: .6;
  cursor: default;
}

.nav.prev {
  left: 12px;
}

.nav.next {
  right: 12px;
}

.btn-label {
  font-size: 14px;
}

@media (max-width: 799px) {
  .viewer {
    width: calc(100% - 16px);
    max-height: 92vh;
  }

  .viewer-body {
    height: 68vh;
  }
}

/* Mobile-Variante: bis 600px */
@media (max-width: 600px) {
  .footer-left {
    display: none;
  }

  .footer-right {
    display: grid;
    width: 100%;
    gap: 6px;
    grid-template-columns: 1fr 2.4fr 1fr 1fr;
  }

  .footer-right .btn {
    display: inline-grid;
  }

  .footer-right .btn-label {
    display: none;
  }

  .footer-right .btn-group .btn {
    width: 100%;
  }
}
</style>
